import {
  IChargeError,
  IPageableRechargeChargeResponse,
  IRechargeCharge,
  IRechargeChargeGetParams,
  ISingleRechargeChargeResponse,
  TRechargeChargeResult,
} from "@recharge";
import { Logger } from "@nestjs/common";
import axios, { AxiosError } from "axios";

import { RechargeResourceEndpoint } from "../../RechargeEndpoint";
import { RechargeAccessTokenManager } from "../RechargeAccessTokenManager";

export class RechargeChargesClient extends RechargeResourceEndpoint<
ISingleRechargeChargeResponse,
IPageableRechargeChargeResponse,
IRechargeChargeGetParams,
IRechargeCharge
> {

  public constructor(
    rechargeKeyManager: RechargeAccessTokenManager,
    baseUrl = "https://api.rechargeapps.com",
  ) {
    super(
      axios.create({ baseURL: baseUrl }),
      rechargeKeyManager.getNextAccessToken.bind(rechargeKeyManager),
      "/charges",
      new Logger(RechargeChargesClient.name),
    );
  }

  protected override singleGetter(t: ISingleRechargeChargeResponse): IRechargeCharge {
    return t.charge;
  }

  protected override multiGetter(t: IPageableRechargeChargeResponse): IRechargeCharge[] {
    return t.charges;
  }

  /** Will immediately cause the given queued charge to be charged and the corresponding order to be generated.
   * @param rechargeChargeId the ID of the queued ReCharge charge to process
   * @param retryTimeoutsAndConflicts if set to true,
   * then result codes that indicate timeouts and conflicts will be retried.
   * Else, they will result in an immediate failure.
   * This should be set to true when processing checkouts,
   * where we really want to try as hard as possible to check out, and false for everything else.
   * That is because ReCharge doesn't lock the customer properly during charge attempts,
   * so retrying charges can result in undefined behaviour
   * (e.g. the charge may be charged successfully but no order will be generated).
   * We can tolerate that risk for checkouts, since it's pretty rare,
   * but we don't have to take that risk for recurring charges,
   * since if they fail, they'll be retried later anyway.
   * @return the charge after it has been processed. */
  public async processCharge(
    rechargeChargeId: number,
    retryTimeoutsAndConflicts: boolean,
  ): Promise<TRechargeChargeResult> {
    try {
      const response = await this.requestWithRetry<ISingleRechargeChargeResponse, Record<string, never>>(
        {
          url: `${this.resourcePath}/${rechargeChargeId}/process`,
          config: {
            headers: this.getHeaders(),
            method: "POST",
            data: {},
          },
          newKeyOnRetry: !retryTimeoutsAndConflicts,
          // Retrying charges is bad because ReCharge can have undefined behaviour when it comes to that thing
          // But that doesn't happen often.
          // We tolerate it during checkout, but not during recurring charges.
          retryGatewayTimeout: retryTimeoutsAndConflicts,
          retryConflict: retryTimeoutsAndConflicts,
          truncateLongBody: false,
        },
      );
      return {
        status: "success",
        value: response.data.charge,
      };
    } catch (cause) {
      if (axios.isAxiosError(cause)) {
        const axiosError: AxiosError = cause;
        if (axiosError.response?.status === 422
          && typeof axiosError.response?.data === "object"
          && axiosError.response.data) {
          const responseData = axiosError.response.data;
          const responseDataErrors = "errors" in responseData
          && typeof responseData.errors === "object"
          && responseData.errors
            ? Object.entries(responseData.errors)
            : [];
          const errors: IChargeError[] = responseDataErrors.flatMap(([ errorType, value ]) => {
            const errorArray = Array.isArray(value)
              ? value
              : [ value ];
            return errorArray.map((errorMessage) => (typeof errorMessage === "string"
              ? { errorType, ...removeReq(errorMessage) }
              : { errorType, errorMessage: JSON.stringify(errorMessage) }));
          });
          const message = `Failed to process charge ${
            rechargeChargeId
          }: ${JSON.stringify(errors)}`;
          const { error, otherErrors } = splitErrors(errors);
          this.logger.error(message, {
            rechargeChargeId,
            cause,
            error,
            otherErrors,
            responseData,
          });
          if (error) {
            return {
              status: "charge_error",
              error,
              otherErrors,
            };
          }
          throw new Error(message, { cause });
        }
      }
      throw cause;
    }
  }

  /** Will remove any discount applied to the given charge.
   * Will fail with 422 if there is no discount (and for other configuration errors).
   * @param rechargeChargeId the charge from which the discount should be removed
   * @param commit if true, the charge MIGHT get regenerated more quickly,
   * but the request might time out if regeneration takes too long.
   * @return the updated charge after the discount has been removed */
  public async removeDiscount(
    rechargeChargeId: number,
    commit: true | undefined,
  ): Promise<IRechargeCharge> {
    const response = await this.requestWithRetry<ISingleRechargeChargeResponse, Record<string, never>>(
      {
        url: `${this.resourcePath}/${rechargeChargeId}/remove_discount`,
        config: {
          headers: this.getHeaders(),
          method: "POST",
          data: {},
          params: {
            commit,
          },
        },
        newKeyOnRetry: true,
        retryGatewayTimeout: true,
        retryConflict: true,
        truncateLongBody: false,
      },
    );
    return response.data.charge;
  }

  /** Will add the given discount to the given charge.
   * Will fail with 422 if there is already a discount applied (and for other configuration errors).
   * @param rechargeChargeId the ReCharge charge to which the discount should be added
   * @param rechargeDiscountId the ReCharge discount that should be added to the charge
   * @param commit if true, the charge MIGHT get regenerated more quickly,
   * but the request might time out if regeneration takes too long.
   * @return the updated charge after the discount has been added */
  public async addDiscount(
    rechargeChargeId: number,
    rechargeDiscountId: number,
    commit: true | undefined,
  ): Promise<IRechargeCharge> {
    const response = await this.requestWithRetry<
    ISingleRechargeChargeResponse,
    { discount_id: number } | { discount_code: string }
    >(
      {
        url: `${this.resourcePath}/${rechargeChargeId}/apply_discount`,
        config: {
          headers: this.getHeaders(),
          method: "POST",
          data: {
            discount_id: rechargeDiscountId,
          },
          params: {
            commit,
          },
        },
        newKeyOnRetry: true,
        retryGatewayTimeout: true,
        retryConflict: true,
        truncateLongBody: false,
      },
    );
    return response.data.charge;
  }

}

function removeReq(inErrorMessage: string): {
  errorMessage: string;
  req: string | undefined;
} {
  const result = /^(Request (req_[a-zA-Z0-9]+):)?(.*)$/s.exec(inErrorMessage);
  const errorMessage = result?.at(3)?.trim() || inErrorMessage;
  const req = result?.at(2)?.trim() || undefined;
  return { errorMessage, req };
}

function splitErrors(errors: IChargeError[]): {
  error: IChargeError | undefined;
  otherErrors: IChargeError[] | undefined;
} {
  if (errors.length < 1) return {
    error: undefined,
    otherErrors: undefined,
  };
  // Prefer payment errors if there are multiple errors
  const paymentErrorIndex = errors.findIndex((e) => e.errorType === "payment");
  if (paymentErrorIndex < 0) {
    return {
      error: errors[0],
      otherErrors: errors.length > 1 ? errors.slice(1) : undefined,
    };
  }
  const otherErrors = errors.slice();
  otherErrors.splice(paymentErrorIndex, 1);
  return {
    error: errors[paymentErrorIndex],
    otherErrors: otherErrors.length > 0 ? otherErrors : undefined,
  };
}
